import React, { useState, useEffect } from 'react';
import './Cart.css';
import DesktopCartItem from './DesktopCartItem';
import MobileCartItem from './MobileCartItem';
import { getApiUrl } from '../../config/api';
import TermsModal from './TermsModal';
import { useQuery } from '@tanstack/react-query';

// 需求澄清：
// 1. 從 localStorage 取得 cart（array，內容為 barcode 字串）
// 2. 以 POST 方式向 /orders/get_multiple_barcode_product_info 取得商品資訊，body 格式為 { barcode_list: [...] }
// 3. 取得資料後需考慮 response 可能為「物件陣列」格式（如新API格式），需正確對應欄位
// 4. 顯示 loading 狀態、空購物車狀態、錯誤處理

function Cart() {
  const [cartBarcodes, setCartBarcodes] = useState([]); // barcode 陣列
  const [cartNumber, setCartNumber] = useState({}); // { barcode: 數量 }
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [textFileContent, setTextFileContent] = useState('');
  const [showTextFile, setShowTextFile] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(true);
  const [isCopying, setIsCopying] = useState(false);

  // 監聽視窗寬度變化
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 初始化：讀取 cart 和 cartNumber
  useEffect(() => {
    try {
      const cartValue = localStorage.getItem('cart');
      let barcodes = [];
      if (cartValue) {
        if (typeof cartValue === 'string') {
          barcodes = JSON.parse(cartValue);
        } else {
          barcodes = cartValue;
        }
      }
      setCartBarcodes(Array.isArray(barcodes) ? barcodes : []);
      // 讀取 cartNumber
      const cartNumberValue = localStorage.getItem('cartNumber');
      let cartNumObj = {};
      if (cartNumberValue) {
        cartNumObj = JSON.parse(cartNumberValue);
      }
      setCartNumber(cartNumObj && typeof cartNumObj === 'object' ? cartNumObj : {});
    } catch (e) {
      setCartBarcodes([]);
      setCartNumber({});
    }
  }, []);

  // 每次 cartBarcodes 變動時，補齊 cartNumber 條目，並移除多餘條目
  useEffect(() => {
    if (!Array.isArray(cartBarcodes)) return;
    setCartNumber(prev => {
      let cartNumObj = { ...prev };
      // 補齊 barcode 條目
      cartBarcodes.forEach(bc => {
        if (cartNumObj[bc] === undefined) cartNumObj[bc] = 1;
      });
      // 移除多餘條目
      Object.keys(cartNumObj).forEach(bc => {
        if (!cartBarcodes.includes(bc)) delete cartNumObj[bc];
      });
      localStorage.setItem('cartNumber', JSON.stringify(cartNumObj));
      return cartNumObj;
    });
  }, [cartBarcodes]);

  // 用 React Query 快取查詢商品詳細資料
  const { data: cartProducts = [], isLoading, error } = useQuery({
    queryKey: ['cartProducts', cartBarcodes],
    queryFn: async () => {
      if (!cartBarcodes || cartBarcodes.length === 0) return [];
      const apiUrl = getApiUrl('/orders/get_multiple_barcode_product_info');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ barcodes: cartBarcodes })
      });
      const data = await response.json();
      let products = [];
      if (Array.isArray(data.data) && data.data.length > 0) {
        products = data.data.map(item => ({
          id: item.id,
          barcode: item['條碼'],
          product_name: item['品名'],
          remaining_stock: item['剩餘庫存'],
          visible_quantity: item['現貨系統可販售數'],
          system_price: item['現貨系統價格'],
          system_store: item['現貨系統活動'],
          producer: item['出品商'],
          series: item['系列'],
          product_type: item['類型'],
          role1: item['角色1'],
          role2: item['角色2'],
          others: item['其他'],
          imageUrl: `https://cdn.jsdelivr.net/gh/WaynezProg/product_picture/all/${item['條碼']}.webp`,
        }));
      }
      return products;
    },
    enabled: cartBarcodes.length > 0,
    staleTime: 30 * 60 * 1000 // 30分鐘
  });

  // 數量調整
  const handleQtyChange = (item, delta) => {
    setCartNumber(prev => {
      const oldQty = prev[item.barcode] !== undefined ? prev[item.barcode] : 1;
      const newQty = Math.max(1, Math.min(oldQty + delta, 99));
      const updated = { ...prev, [item.barcode]: newQty };
      localStorage.setItem('cartNumber', JSON.stringify(updated));
      return updated;
    });
  };

  // 移除商品
  const handleRemove = (item) => {
    if (cartBarcodes.length === 0) return;
    if (window.confirm('確定要移除這個商品嗎？')) {
      setCartBarcodes(prev => {
        const updatedBarcodes = prev.filter(b => b !== item.barcode);
        localStorage.setItem('cart', JSON.stringify(updatedBarcodes));
        return updatedBarcodes;
      });
      setCartNumber(prev => {
        const updated = { ...prev };
        delete updated[item.barcode];
        localStorage.setItem('cartNumber', JSON.stringify(updated));
        return updated;
      });
    }
  };

  // 計算總價
  const getTotal = () => {
    return cartProducts.reduce((sum, item) => {
      const qty = cartNumber[item.barcode] !== undefined ? cartNumber[item.barcode] : 1;
      const price = Number(item.system_price || item['現貨系統價格'] || item.price || 0);
      return sum + price * qty;
    }, 0);
  };

  // 產生購物車文字內容
  const generateCartText = () => {
    if (!cartProducts || cartProducts.length === 0) return '';
    let text = '願望清單\n--------------------\n';
    cartProducts.forEach(item => {
      const qty = cartNumber[item.barcode] !== undefined ? cartNumber[item.barcode] : 1;
      const price = Number(item.system_price || item['現貨系統價格'] || item.price || 0);
      text += `品項名稱：${item.product_name}\n條碼：${item.barcode}\n`;
      if (item.system_store && item.system_store.includes("活動")) {
        text += `活動：${item.system_store.split(".")[1]}\n`;
      }
      text += `數量：${qty}\n參考金額：${price}\n小計：${price * qty}\n\n`;
    });
    text += `參考總計：${getTotal()} 元\n--------------------\n`;
    text += `使用條款同意狀態：${termsAgreed ? '已同意' : '未同意'}\n`;
    text += `貨況與活動折扣將於官方帳號中確認`;
    return text;
  };

  // 處理返回上一頁
  const handleBack = (e) => {
    e.preventDefault();
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  const handleTermsAgree = () => {
    setTermsAgreed(true);
  };

  // 處理複製文字並跳轉
  const handleCopyAndNavigate = async (url, actionName) => {
    if (isCopying) return; // 防止重複點擊
    
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(textFileContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      
      // 延遲跳轉，讓用戶看到複製成功的提示
      setTimeout(() => {
        if (url) {
          window.open(url, "_blank");
        }
      }, 500);
    } catch (error) {
      console.error('複製失敗:', error);
      alert('複製失敗，請手動複製文字內容');
    } finally {
      setIsCopying(false);
    }
  };

  // UI
  return (
    <div className="cart-container">
      <h2 className="cart-title">願望清單</h2>
      {isLoading ? (
        <div>
          <div className="loader"></div>
          <div className="loader-text">讀取中，請稍候...</div>
        </div>
      ) : error ? (
        <div className="cart-error">商品資訊取得失敗，請稍後再試。</div>
      ) : cartProducts.length === 0 ? (
        <div className="cart-empty">您的願望清單是空的。</div>
      ) : (
        <>
          <div className="cart-list">
            {cartProducts.map((item, idx) => {
              const qty = cartNumber[item.barcode] !== undefined ? cartNumber[item.barcode] : 1;
              return isMobile ? (
                <MobileCartItem
                  key={item.barcode || idx}
                  item={{ ...item, qty }}
                  onQtyChange={handleQtyChange}
                  onRemove={handleRemove}
                />
              ) : (
                <DesktopCartItem
                  key={item.barcode || idx}
                  item={{ ...item, qty }}
                  onQtyChange={handleQtyChange}
                  onRemove={handleRemove}
                />
              );
            })}
          </div>
          <div className="cart-summary">
            <div className="cart-summary-total-label">參考總計</div>
            <div className="cart-summary-total-value">
              {getTotal().toLocaleString()} <span className="cart-summary-tax">元</span>
            </div>
            <div className="cart-summary-shipping">貨況與活動折扣將於官方帳號中確認</div>
          </div>
          <div className="cart-agreement">
            <span>若您傳送願望清單時，視為您已閱讀並同意使用條款。</span>
            <button
              className="cart-agreement-link"
              onClick={() => setShowTermsModal(true)}
            >
              使用條款
            </button>
            <label className="cart-agreement-checkbox">
              <input 
                type="checkbox" 
                checked={true}
                onChange={() => {}} // 空函數，防止狀態改變
              /> 
              我已同意
            </label>
            <div className="generate-text-container">
              <button
                className="generate-text-btn"
                onClick={() => {
                  setTextFileContent(generateCartText());
                  setShowTextFile(true);
                }}
              >
                下一步，生成願望清單文字檔
              </button>
            </div>
            {showTextFile && (
              <div className="text-file-container">
                <textarea
                  value={textFileContent}
                  readOnly
                  className="text-file-textarea"
                />
                {copySuccess && <span className="copy-success">已複製！</span>}
                <div className="cart-line-link">
                  請加入我們的官方帳號，這樣我們才能找到您喔
                </div>
                <div className="text-actions-container">
                  <button
                    className="text-action-btn text-action-btn-primary"
                    onClick={() => handleCopyAndNavigate("https://lin.ee/YRbJFy7", "複製文字，前往LINE官方帳號私訊")}
                    disabled={isCopying}
                  >
                    {isCopying ? "處理中..." : "複製文字\n前往LINE官方帳號私訊"}
                  </button>
                  <button
                    className="text-action-btn text-action-btn-secondary"
                    onClick={() => handleCopyAndNavigate("https://forms.gle/Cg1GnnVKK9ZGDS7q9", "複製文字，前往表單填寫")}
                    disabled={isCopying}
                  >
                    {isCopying ? "處理中..." : "複製文字\n前往表單填寫"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="cart-checkout-container">
          </div>

          <div className="cart-actions">
            <button
              type="button"
              className="cart-back-link back-link"
              onClick={handleBack}
            >
              返回上一頁
            </button>
            <button
              className="cart-clear-btn"
              onClick={() => {
                if (window.confirm('確定要清空願望清單嗎？')) {
                  localStorage.removeItem('cart');
                  localStorage.removeItem('cartNumber');
                  setCartBarcodes([]);
                  setCartNumber({});
                }
              }}
            >
              清空願望清單
            </button>
          </div>
        </>
      )}
      <TermsModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)}
        onAgree={handleTermsAgree}
      />
    </div>
  );
}

export default Cart;